import { Check, ChevronsUpDown } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@nucleus-commerce/ui/components/command';
import { Popover, PopoverContent, PopoverTrigger } from '@nucleus-commerce/ui/components/popover';
import { cn } from '@nucleus-commerce/ui/lib/utils';

import { supportedCurrencies } from '../schemas/currencies';

export interface CurrencySelectProps {
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  /** Codes offered first, e.g. the store's supported currencies. */
  preferred?: readonly string[];
  /** Heading of the preferred group; defaults to "Supported by this store". */
  preferredLabel?: string;
  disabled?: boolean;
  id?: string;
  'aria-invalid'?: boolean;
}

/** Searchable ISO 4217 picker; a typo in a free-text field used to reach the server as a new currency. */
export function CurrencySelect({
  value,
  onChange,
  onBlur,
  preferred = [],
  preferredLabel,
  disabled,
  id,
  'aria-invalid': ariaInvalid,
}: CurrencySelectProps) {
  const { t } = useTranslation('stores');
  const [open, setOpen] = useState(false);
  const all = useMemo(() => supportedCurrencies(), []);
  const first = all.filter((c) => preferred.includes(c.code));
  const rest = first.length > 0 ? all.filter((c) => !preferred.includes(c.code)) : all;
  const current = all.find((c) => c.code === value);

  const item = (code: string, name: string) => (
    <CommandItem
      key={code}
      value={code}
      keywords={[name]}
      onSelect={() => {
        onChange(code);
        setOpen(false);
      }}
    >
      <Check className={cn('size-4', code === value ? 'opacity-100' : 'opacity-0')} aria-hidden />
      <span className="font-mono text-[13px]">{code}</span>
      <span className="text-muted-foreground flex-1 truncate text-xs">{name}</span>
    </CommandItem>
  );

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) onBlur?.();
      }}
    >
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          aria-invalid={ariaInvalid}
          disabled={disabled}
          className={cn('w-full justify-between font-normal', !value && 'text-muted-foreground')}
        >
          <span className="truncate">
            {value ? (
              <>
                <span className="font-mono text-[13px]">{value}</span>
                {current ? (
                  <span className="text-muted-foreground ml-2 text-xs">{current.name}</span>
                ) : null}
              </>
            ) : (
              t('currency.placeholder')
            )}
          </span>
          <ChevronsUpDown className="size-4 shrink-0 opacity-60" aria-hidden />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] min-w-72 p-0" align="start">
        <Command>
          <CommandInput placeholder={t('currency.search')} />
          <CommandList className="max-h-72">
            <CommandEmpty>{t('currency.empty')}</CommandEmpty>
            {first.length > 0 ? (
              <CommandGroup heading={preferredLabel ?? t('currency.supportedGroup')}>
                {first.map((c) => item(c.code, c.name))}
              </CommandGroup>
            ) : null}
            <CommandGroup heading={first.length > 0 ? t('currency.allGroup') : undefined}>
              {rest.map((c) => item(c.code, c.name))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
